/**
 * Feature 14 — AccountManagerStore (referans-parite multi-account).
 *
 * localStorage'da tek `access_token`/`refresh_token` yerine `accounts[]` array
 * + `currentAccountId` saklar. Kullanıcı birden fazla hesap arasında oturum
 * kapatmadan geçebilir.
 *
 * Migration (constructor):
 *   - Eski "access_token"/"refresh_token" localStorage key'leri varsa
 *     accounts[0] olarak adapt et (id="legacy-<rand>"), eski key'leri sil.
 *   - Migration tek-shot — `tebhos-account-manager` key set olduktan sonra
 *     tekrar tetiklenmez.
 *
 * apiFetch token kaynağı: `getCurrentAccessToken()` / `getCurrentRefreshToken()`
 * — eski helper'lar bu store'a delege.
 *
 * ÇOKLU-HESAP İZOLASYONU (2026-07-18, kaynak-referans `AccountStorage.ts` portu — bkz.
 * `features/auth/state/AccountStorage.ts`): her hesabın taslak/arama-geçmişi/
 * tercih gibi "yönetilen" localStorage anahtarları artık hesap-başına
 * IndexedDB'de anlık-görüntü olarak saklanır. `addAccount` (farklı bir hesaba
 * giriş) ve `switchAccount` bu anlık-görüntüyü stash/restore eder — kaynak-referans
 * `SessionManager.login`/`switchAccount` 1-1 (sadeleştirilmiş: ayrı bir
 * state-machine yok, doğrudan çağrılır). Sunucu-senkron tercihler
 * (SyncedPreferencesEngine/SyncedField, C1 ajanı) bu mekanizmanın DIŞINDA —
 * onlar zaten hesapla birlikte sunucudan gelir, yerel anlık-görüntü yalnız
 * saf-yerel tercihler/taslaklar içindir.
 *
 * TOKEN-COOKIE SÖKÜMÜ (2026-07-18, İŞ 2): `document.cookie`'ye access/refresh
 * token yazımı (eski writeCookie/syncCookies) KALDIRILDI. Araştırma kanıtı:
 * hiçbir backend/middleware/proxy (tebhos_api/middleware/auth_guard.rs,
 * tebhos_app_proxy/* rota'ları, rspack.config.mjs) bu cookie'leri OKUMUYOR —
 * kaynak-referansta da cookie kullanımı SIFIR. Tek gerçek token kaynağı zaten bu
 * store (getCurrentAccessToken → Authorization header, lib/api/client.ts).
 * Geriye dönük temizlik: `purgeStaleAuthCookies()` mevcut tarayıcılardaki
 * bayat cookie'leri bir kez siler.
 *
 * MobX geçişi (zustand→mobx, kaynak-referans `features/auth/state/AccountManager.ts`
 * konvansiyonu): `makeAutoObservable` singleton sınıf + `makePersistent`
 * (AYNI storage key — eski zustand `{"state":{...}}` sarmalı geriye dönük
 * okunur, mevcut kullanıcı verisi kaybolmaz). kaynak-referansın SessionManager'lı
 * modeli farklıdır; TEBHOS'a özgü token-kasası davranışı KORUNDU.
 */

import { makeAutoObservable, runInAction } from "mobx";
import { makePersistent } from "@app/features/platform/utils/MobXPersistence";
import AccountStorage from "@app/features/auth/state/AccountStorage";
import LocalPresence from "@app/features/presence/state/LocalPresence";
import { Logger } from "@app/features/platform/utils/Logger";

const logger = new Logger("AccountManagerStore");

const LEGACY_ACCESS_KEY = "access_token";
const LEGACY_REFRESH_KEY = "refresh_token";

/** Zustand persist(name) ile birebir aynı — mevcut kullanıcı verisi devralınır. */
const STORAGE_KEY = "tebhos-account-manager";

export interface AccountEntry {
  /** Unique stable id — backend'den profile id veya migration sentinel. */
  id: string;
  /** Display name (cached); login sonrası set edilir. */
  name: string | null;
  /** Avatar URL — switcher card için. */
  imageUrl: string | null;
  /**
   * Kullanıcı adı + ayırıcı — hesap satırı kaynak-referans gibi
   * `Görünen Ad kullanıcıadı#0000` çizsin diye. Aktif olmayan hesapların
   * kullanıcı kaydı bellekte bulunmadığından giriş/devir anında saklanır.
   * Eski kayıtlarda yok (isteğe bağlı).
   */
  username?: string | null;
  discriminator?: string | null;
  /** Access JWT (~15dk TTL). */
  accessToken: string;
  /** Refresh JWT (~7gün TTL). */
  refreshToken: string;
  /** Eklenme anı (display "X gün önce" için). */
  addedAt: string;
}

/**
 * İŞ 2 (2026-07-18) — eski `access_token`/`refresh_token` cookie'lerini bir
 * kez temizler. Bu cookie'ler daha önce her login/switch/logout'ta yazılıyordu
 * ama HİÇBİR taraf (backend auth_guard, app_proxy, rspack dev-proxy) onları
 * okumuyordu — ölü yazım. Bayat cookie bırakmamak için tek-seferlik silme.
 */
function purgeStaleAuthCookies(): void {
  if (typeof document === "undefined") return;
  for (const name of [LEGACY_ACCESS_KEY, LEGACY_REFRESH_KEY]) {
    document.cookie = `${name}=; path=/; max-age=0`;
  }
}

class AccountManager {
  accounts: AccountEntry[] = [];
  currentAccountId: string | null = null;
  /** Async switchAccount sürüyor mu (kaynak-referans `SessionManager.isSwitching` sadeleşmiş karşılığı). */
  isSwitchingAccount = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    // Hidrasyon senkron çalışır (Promise yalnız API paritesi) — hemen
    // ardından legacy migration güvenle değerlendirilebilir.
    void makePersistent(this, STORAGE_KEY, ["accounts", "currentAccountId"]);
    this.migrateLegacyTokens();
    purgeStaleAuthCookies();
  }

  /**
   * Çıkılan hesabın (varsa) presence-niyetini yakalar + O ANKİ yönetilen
   * localStorage anlık-görüntüsünü IndexedDB'ye stash'ler. kaynak-referans
   * `prepareForAccountTransition` + `stashCurrentAccount` 1-1 sadeleştirilmiş
   * karşılığı — TEBHOS'ta ayrı bir state-machine yok, doğrudan çağrılır.
   * Best-effort: IDB hatası hesap değişimini ENGELLEMEZ, yalnız log'lanır.
   */
  private stashAccountSnapshot(userId: string): void {
    const account = this.accounts.find((a) => a.id === userId);
    const presenceIntent = LocalPresence.captureIntent();
    void AccountStorage.stashAccountData(
      userId,
      account?.accessToken ?? null,
      account?.refreshToken ?? null,
      account ? { name: account.name, imageUrl: account.imageUrl } : undefined,
      presenceIntent,
    ).catch((err) => logger.warn(`Hesap ${userId} stash edilemedi`, err));
  }

  /**
   * Eski tekil `access_token`/`refresh_token` localStorage key'lerini
   * accounts[0] olarak devralır (zustand `onRehydrateStorage` karşılığı).
   * accounts zaten doluysa (migration yapılmış) atlar.
   */
  private migrateLegacyTokens(): void {
    if (typeof window === "undefined") return;
    if (this.accounts.length > 0) return;

    const access = window.localStorage.getItem(LEGACY_ACCESS_KEY);
    const refresh = window.localStorage.getItem(LEGACY_REFRESH_KEY);
    if (!access || !refresh) return;

    const id = `legacy-${Math.random().toString(36).slice(2, 10)}`;
    const entry: AccountEntry = {
      id,
      name: null,
      imageUrl: null,
      accessToken: access,
      refreshToken: refresh,
      addedAt: new Date().toISOString(),
    };
    this.accounts = [entry];
    this.currentAccountId = id;
    // Eski key'leri sil — bir daha migrate edilmesin.
    window.localStorage.removeItem(LEGACY_ACCESS_KEY);
    window.localStorage.removeItem(LEGACY_REFRESH_KEY);
    this.stashAccountSnapshot(id);
  }

  /**
   * Hesap ekler (aynı id varsa replace) ve aktif hesap yapar. kaynak-referans
   * `SessionManager.login` 1-1: farklı bir hesaptan geliniyorsa (aynı
   * tarayıcıda "Hesap Ekle" ile yeni login), ÖNCE çıkılan hesabın anlık
   * localStorage görüntüsü IndexedDB'ye stash edilir — böylece o hesabın
   * taslak/tercihleri kaybolmaz, sıradaki switchAccount çağrısında geri gelir.
   * (Bilinen referans-parite kenar-durum: yeni eklenen hesap, ilk anda
   * localStorage'da fiziksel olarak ÖNCEKİ hesabın verisini görür — bu ilk
   * gerçek switchAccount döngüsünde temizlenir; kaynak-referans da aynen böyle davranır.)
   */
  addAccount(account: Omit<AccountEntry, "addedAt">): void {
    const previousId = this.currentAccountId;
    if (previousId && previousId !== account.id) {
      this.stashAccountSnapshot(previousId);
    }
    const entry: AccountEntry = { ...account, addedAt: new Date().toISOString() };
    const filtered = this.accounts.filter((a) => a.id !== entry.id);
    this.accounts = [...filtered, entry];
    this.currentAccountId = entry.id;
    this.stashAccountSnapshot(entry.id);
  }

  /** Hesabı kaldırır; aktif hesapsa sıradaki hesaba (yoksa null) geçer. */
  removeAccount(id: string): void {
    const remaining = this.accounts.filter((a) => a.id !== id);
    const newCurrent = this.currentAccountId === id ? (remaining[0]?.id ?? null) : this.currentAccountId;
    this.accounts = remaining;
    this.currentAccountId = newCurrent;
    void AccountStorage.deleteAccount(id).catch((err) => logger.warn(`Hesap ${id} storage'dan silinemedi`, err));
  }

  /**
   * Aktif hesabı değiştirir — kaynak-referans `SessionManager.switchAccount` 1-1:
   *   1) çıkılan hesabın presence-niyeti + yönetilen localStorage anlık-
   *      görüntüsü IndexedDB'ye stash'lenir
   *   2) hedef hesabın stash'lenmiş anlık-görüntüsü localStorage'a UYGULANIR
   *      (bulunmayan yönetilen anahtarlar silinir — applyManagedStorageSnapshot)
   *   3) presence-niyeti geri yüklenir, currentAccountId güncellenir
   * Bilinmeyen id veya zaten aktif hesap → no-op. IDB hatası olsa da hesap
   * geçişi devam eder (best-effort — kullanıcıyı kilitli bırakmamak için).
   */
  async switchAccount(id: string): Promise<void> {
    const target = this.accounts.find((a) => a.id === id);
    if (!target || id === this.currentAccountId) return;
    // mobx: makeAutoObservable yalnız senkron gövdeyi otomatik action'a sarar;
    // ilk `await`ten SONRAKİ mutasyonlar strict-mode'da observable'ı action
    // DIŞINDA değiştirir uyarısı verir — runInAction ile açıkça sarmalanır.
    runInAction(() => {
      this.isSwitchingAccount = true;
    });
    try {
      const outgoingId = this.currentAccountId;
      if (outgoingId) {
        const outgoing = this.accounts.find((a) => a.id === outgoingId);
        const presenceIntent = LocalPresence.captureIntent();
        try {
          await AccountStorage.stashAccountData(
            outgoingId,
            outgoing?.accessToken ?? null,
            outgoing?.refreshToken ?? null,
            outgoing ? { name: outgoing.name, imageUrl: outgoing.imageUrl } : undefined,
            presenceIntent,
          );
        } catch (err) {
          logger.warn(`Hesap ${outgoingId} geçiş-öncesi stash edilemedi`, err);
        }
      }
      let restored: Awaited<ReturnType<typeof AccountStorage.restoreAccountData>> = null;
      try {
        restored = await AccountStorage.restoreAccountData(id);
      } catch (err) {
        logger.warn(`Hesap ${id} restore edilemedi`, err);
      }
      runInAction(() => {
        this.currentAccountId = id;
      });
      LocalPresence.restoreIntent(restored?.presenceIntent ?? null);
    } finally {
      runInAction(() => {
        this.isSwitchingAccount = false;
      });
    }
  }

  /** Aktif hesabın token çiftini günceller (refresh akışı). */
  updateCurrentTokens(access: string, refresh: string): void {
    if (!this.currentAccountId) return;
    this.accounts = this.accounts.map((a) =>
      a.id === this.currentAccountId ? { ...a, accessToken: access, refreshToken: refresh } : a,
    );
  }

  /** Hesabın görünen ad/avatar cache'ini günceller. */
  updateAccountProfile(
    id: string,
    patch: Partial<Pick<AccountEntry, "name" | "imageUrl" | "username" | "discriminator">>,
  ): void {
    this.accounts = this.accounts.map((a) => (a.id === id ? { ...a, ...patch } : a));
  }

  /** Aktif hesap kaydı (yoksa null). */
  getCurrent(): AccountEntry | null {
    if (!this.currentAccountId) return null;
    return this.accounts.find((a) => a.id === this.currentAccountId) ?? null;
  }

  /** Birden fazla kayıtlı hesap var mı? (switcher görünürlüğü) */
  hasMultiple(): boolean {
    return this.accounts.length > 1;
  }
}

/** Multi-account token kasası singleton'ı (kaynak-referans AccountManager paritesi). */
export const AccountManagerStore = new AccountManager();
export default AccountManagerStore;

/**
 * apiFetch + Auth helper'ları için public token getter'ları.
 * Eski `getAccessToken`/`getRefreshToken`/`setTokens`/`clearTokens` artık
 * bu fonksiyonlara delege eder.
 */
export function getCurrentAccessToken(): string | null {
  return AccountManagerStore.getCurrent()?.accessToken ?? null;
}

export function getCurrentRefreshToken(): string | null {
  return AccountManagerStore.getCurrent()?.refreshToken ?? null;
}

export function setCurrentTokens(access: string, refresh: string) {
  AccountManagerStore.updateCurrentTokens(access, refresh);
}

export function clearCurrentAccount() {
  const id = AccountManagerStore.currentAccountId;
  if (id) AccountManagerStore.removeAccount(id);
}
